<template>
  <ul v-if="identityLoaded" class="breadcrumb text-racq-dark-grey pb-8">
    <li>
      <a :href="pageStore.homePage.value" class="no-underline hover:!underline hover:!decoration-[10%]">{{ pageStore.homePage.name }}</a>
    </li>
    <li v-for="(breadcrumb, index) in linkProps" :key="index">
      <a v-bind="breadcrumb" class="no-underline hover:!underline hover:!decoration-[10%]">{{ breadcrumb.title }}</a>
    </li>
  </ul>
</template>
<script setup lang="ts">
import { usePageStore, useAccountStore, useRoadsideAssistance } from '@/store';
import { useRoute } from 'vue-router';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { RoadsideProductType } from '@/utils/roadside-helpers';
import { useI18n } from 'vue-i18n';
import { getInsuranceTypeDisplayName } from '@/interfaces';
import { stringify } from 'querystring';
import { computedAsync } from '@vueuse/core';
import { computed } from 'vue';
import { showCombined } from '@/utils/common-helper';

enum Bookmarks {
  tierDescription = 'tierDescription',
  policyId = 'policyId',
}

const pageStore = usePageStore();
const accountStore = useAccountStore();
const rsaStore = useRoadsideAssistance();
const route = useRoute();

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
const linkProps = computedAsync(() => updateBreadCrumbs(), [{ title: 'Loading...' }], { lazy: true });

// Wait for identity to avoid policy not found
const identityLoaded = computed(() => accountStore.identityDetails?.portfolio);

/**
 * Replace breadcrumb title and url bookmarks
 * @ref Bookmarks
 */
const updateBreadCrumbs = async () => {
  const breadcrumbs: Record<string, any>[] = [];
  for (const index in pageStore.breadcrumbs) {
    const item = pageStore.breadcrumbs[index];
    // Replace bookmark in href e.g {{policyId}}
    if (item.value?.includes(Bookmarks.policyId))
      item.value = replaceBookmark(Bookmarks.policyId, item.value, route.query?.policyId as string);

    // Replace bookmark in label {{tierDescription}} => RACQ Everyday
    const title = await getBreadCrumbTitle(item.name);

    // Special case for roadside details / insurance details sub pages
    if (item.name != title)
      item.value += `${item.value?.includes('?') ? '&' : '?'}${stringify((route.query as Record<string, string>) ?? {})}`;

    const bindings: Record<string, any> = {
      href: item.value,
      title,
    };
    if (Number(index) == pageStore.breadcrumbs.length - 1) bindings.disabled = true;
    breadcrumbs.push(bindings);
  }
  return breadcrumbs;
};

/**
 * Convert breadcrumb title to policy name
 * @param breadcrumbTitle 00312321
 * @return RACQ Everyday
 */
const getBreadCrumbTitle = async (breadcrumbTitle: string) => {
  if (!breadcrumbTitle?.includes(Bookmarks.tierDescription)) return breadcrumbTitle;

  try {
    if (route.query.policyId || route.query.policyid) {
      const usePolicyId = route.query.policyId ?? route.query.policyid;
      const status = route.query.status;
      const rsaPolicy = accountStore.identityDetails?.portfolio?.assistanceProducts?.find(
        (p) =>
          p.policyId === usePolicyId && (!status ? true : p.status?.toLowerCase() === status?.toString()?.toLowerCase())
      );
      const roadsidePolicyType = rsaPolicy?.roadsidePolicyType;
      const tierDescription =
        roadsidePolicyType !== RoadsideProductType.Cmo
          ? rsaPolicy?.tierDescription
          : (await rsaStore.getRsaProductContent(rsaPolicy?.productCode))?.tierDescription;
      return tierDescription
        ? replaceBookmark(Bookmarks.tierDescription, breadcrumbTitle, tierDescription)
        : breadcrumbTitle;
    } else if (route.query.policyNumber || route.query.policynumber) {
      const usePolicyNumber = route.query.policyNumber ?? route.query.policynumber;
      const currentPolicy = accountStore.identityDetails?.portfolio?.insurancePolicies?.find(
        (p) => p.policyNumber === usePolicyNumber
      );

      let insuranceDisplayName;
      if (showCombined(currentPolicy?.coverages ?? [])) {
        insuranceDisplayName = dictionary.IT_Combined;
      } else {
        insuranceDisplayName = getInsuranceTypeDisplayName(
          dictionary,
          currentPolicy?.coverages[0]?.coverageCode ?? '',
          false
        );
      }

      return insuranceDisplayName
        ? replaceBookmark(Bookmarks.tierDescription, breadcrumbTitle, insuranceDisplayName)
        : breadcrumbTitle;
    }
  } catch {
    // do nothing
  }
  return breadcrumbTitle;
};
</script>
